import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  BookOpen, 
  CreditCard, 
  Mail,
  Building2,
  FileText,
  BarChart3,
  Activity
} from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [activeUsers, setActiveUsers] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in to access the dashboard.');
      navigate('/signin');
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Only Super Admin, Exlva Admin, or Admin can access
      if (!['Super Admin', 'Exlva Admin', 'Admin'].includes(parsedUser.role)) {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
      
      fetchDashboardStats();
      fetchActiveUsers();
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/signin');
    }
  }, [navigate]);

  // Poll active users every 30 seconds for near real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchActiveUsers();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch real hospital metrics from API
      const response = await axios.get(`${API}/hospitals/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const metrics = response.data;
      setDashboardStats({
        hospitalsOnboarded: (metrics.total_active || 0) + (metrics.deactivated || 0),
        activeHospitals: metrics.total_active || 0,
        inactiveHospitals: metrics.deactivated || 0,
        invoicesBilled: 125000
      });
    } catch (error) {
      console.error('Failed to fetch dashboard stats');
      // Fallback to mock data if API fails
      setDashboardStats({
        hospitalsOnboarded: 5,
        activeHospitals: 3,
        inactiveHospitals: 2,
        invoicesBilled: 125000
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/active-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveUsers(response.data.active_users || 0);
    } catch (error) {
      console.error('Failed to fetch active users count');
    }
  };

  if (loading || !user) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Dashboard Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-8">DASHBOARD</h1>

          {/* Stats Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Hospitals Onboarded Card */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-blue-700 mb-1">Hospitals onboarded</p>
                  <h3 className="text-4xl font-bold text-blue-900">{dashboardStats?.hospitalsOnboarded || 0}</h3>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </div>

            {/* Active Hospitals Card */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-purple-700 mb-1">Active hospitals</p>
                  <h3 className="text-4xl font-bold text-purple-900">{dashboardStats?.activeHospitals || 0}</h3>
                </div>
                <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-purple-700" />
                </div>
              </div>
            </div>

            {/* Inactive Hospitals Card */}
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-pink-700 mb-1">Inactive hospitals</p>
                  <h3 className="text-4xl font-bold text-pink-900">{dashboardStats?.inactiveHospitals || 0}</h3>
                </div>
                <div className="w-12 h-12 bg-pink-200 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-pink-700" />
                </div>
              </div>
            </div>

            {/* Active Users Card */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Active Users</p>
                  <h3 className="text-4xl font-bold text-green-900">{activeUsers}</h3>
                </div>
                <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-700" />
                </div>
              </div>
              <p className="text-xs text-green-600">Currently online</p>
            </div>
          </div>

          {/* Invoices Billed Card - Full Width */}
          <div className="mt-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-teal-700 mb-2">Invoices billed</p>
                <h3 className="text-5xl font-bold text-teal-900">
                  ₹{dashboardStats?.invoicesBilled?.toLocaleString('en-IN') || 0}
                </h3>
                <p className="text-sm text-teal-600 mt-2">Total revenue generated</p>
              </div>
              <div className="w-16 h-16 bg-teal-200 rounded-lg flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-teal-700" />
              </div>
            </div>
          </div>

          {/* Additional Sections */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-[18px] font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">New hospital onboarded</p>
                    <p className="text-xs text-gray-500">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Payment received</p>
                    <p className="text-xs text-gray-500">5 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">New training chapter published</p>
                    <p className="text-xs text-gray-500">1 day ago</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-[18px] font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/crm')}
                  className="w-full justify-start bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                >
                  <Users className="w-5 h-5 mr-3" />
                  Manage CRM
                </Button>
                <Button
                  onClick={() => navigate('/training')}
                  className="w-full justify-start bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <BookOpen className="w-5 h-5 mr-3" />
                  Add Training Chapter
                </Button>
                <Button
                  onClick={() => navigate('/subscriptions/manage')}
                  className="w-full justify-start bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <CreditCard className="w-5 h-5 mr-3" />
                  Manage Subscriptions
                </Button>
                <Button
                  onClick={() => navigate('/admin/email-templates')}
                  className="w-full justify-start bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Mail className="w-5 h-5 mr-3" />
                  Edit Email Templates
                </Button>
              </div>
            </div>
          </div>
        </div>
    </AdminLayout>
  );
};

export default SuperAdminDashboard;
